import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, UserCog, ShieldCheck, ShieldAlert } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import ImpersonateDialog from "@/components/ImpersonateDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Roles that must never appear in a bulk-delete selection — the checkbox
// is disabled at row level and the backend guards against it anyway.
const PROTECTED_ROLES = new Set(["admin", "superadmin"]);

export default function UsersPanel() {
  const { t } = useLang();
  const { user: currentUser, refresh: refreshAuth } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [selected, setSelected] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [report, setReport] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);

  const load = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    setLoading(true);
    try {
      const params = { page: nextPage, page_size: pageSize };
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.items);
      setTotal(data.total || 0);
      if (opts.page && opts.page !== page) setPage(opts.page);
      // Prune stale selections
      setSelected((prev) => new Set([...prev].filter((id) => data.items.some((u) => u.user_id === id))));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setPage(1); load({ page: 1 }); }, [filter.status, pageSize]);  // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (uid) => { await api.post(`/admin/users/${uid}/approve`); toast.success(t("ad.user_approved")); load(); };
  const block = async (uid, cascade = false) => {
    await api.post(`/admin/users/${uid}/block`, null, { params: { cascade } });
    toast.success(cascade ? t("ad.cascade_blocked") : t("ad.user_blocked"));
    load();
  };
  const unblock = async (uid) => { await api.post(`/admin/users/${uid}/unblock`); toast.success(t("ad.user_unblocked")); load(); };

  const selectableUsers = useMemo(
    () => users.filter((u) => !PROTECTED_ROLES.has(u.role)),
    [users]
  );
  const allChecked = selectableUsers.length > 0 && selectableUsers.every((u) => selected.has(u.user_id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(selectableUsers.map((u) => u.user_id)));
  };
  const toggleOne = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const selectedUsers = useMemo(
    () => users.filter((u) => selected.has(u.user_id)),
    [users, selected]
  );

  const openDeleteDialog = () => {
    setConfirmText("");
    setReport(null);
    setDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", {
        user_ids: [...selected],
      });
      setReport(data);
      const del = data.deleted_count || 0;
      const skip = (data.skipped || []).length;
      if (del > 0) toast.success(`Deleted ${del} user${del === 1 ? "" : "s"}${skip ? ` · ${skip} skipped` : ""}`);
      else toast.info(`0 deleted${skip ? ` · ${skip} skipped` : ""}`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const impersonate = (u) => {
    setImpersonateTarget(u);
  };

  const onImpersonateSuccess = async () => {
    await refreshAuth();
    window.location.href = "/";
  };

  return (
    <Card className="p-4 border-border/60">
      <div className="flex flex-wrap gap-2 mb-3">
        {["", "pending", "approved", "blocked"].map((s) => (
          <Button
            key={s || "all"}
            size="sm"
            variant={filter.status === s ? "default" : "outline"}
            onClick={() => setFilter((f) => ({ ...f, status: s }))}
            className="rounded-full"
            data-testid={`admin-users-filter-${s || "all"}`}
          >
            {s ? t(`ad.filter_${s}`) : t("ad.filter_all")}
          </Button>
        ))}
        <div className="ml-auto flex gap-2 items-center">
          {selected.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={openDeleteDialog}
              className="rounded-full"
              data-testid="admin-users-bulk-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete {selected.size} user{selected.size === 1 ? "" : "s"}
            </Button>
          )}
          <Input
            placeholder={t("ad.search_placeholder")}
            value={filter.search}
            onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            className="h-8 w-48"
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load({ page: 1 }))}
            data-testid="admin-users-search"
          />
          <Button size="sm" onClick={() => { setPage(1); load({ page: 1 }); }}>{t("ad.search")}</Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={toggleAll}
                aria-label="Select all users on this page"
                className="h-4 w-4"
                data-testid="admin-users-select-all"
                disabled={selectableUsers.length === 0}
              />
            </TableHead>
            <TableHead>{t("ad.col_name")}</TableHead>
            <TableHead>{t("ad.col_email")}</TableHead>
            <TableHead>{t("ad.col_role")}</TableHead>
            <TableHead>{t("ad.col_status")}</TableHead>
            <TableHead>Consent</TableHead>
            <TableHead>{t("ad.col_referrer")}</TableHead>
            <TableHead>{t("ad.col_actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => {
            const isProtected = PROTECTED_ROLES.has(u.role);
            return (
              <TableRow key={u.user_id} data-state={selected.has(u.user_id) ? "selected" : undefined}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selected.has(u.user_id)}
                    onChange={() => toggleOne(u.user_id)}
                    aria-label={`Select ${u.name}`}
                    disabled={isProtected}
                    title={isProtected ? "Admins/superadmins cannot be bulk-deleted — demote first" : undefined}
                    className="h-4 w-4"
                    data-testid={`admin-users-check-${u.user_id}`}
                  />
                </TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell>
                  {u.is_blocked ? <Badge variant="destructive">{t("ad.tag_blocked")}</Badge>
                    : u.is_approved ? <Badge>{t("ad.tag_approved")}</Badge>
                    : <Badge variant="secondary">{t("ad.tag_pending")}</Badge>}
                </TableCell>
                <TableCell data-testid={`consent-cell-${u.user_id}`}>
                  {u.agreed_to_terms ? (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-400"
                      title={u.agreed_to_terms_at ? `Agreed on ${new Date(u.agreed_to_terms_at).toLocaleString()}` : "Agreed (timestamp unknown)"}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {u.agreed_to_terms_at
                        ? new Date(u.agreed_to_terms_at).toLocaleDateString()
                        : "yes"}
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400"
                      title="This user was created before the community agreement was enforced (legacy)."
                    >
                      <ShieldAlert className="h-3.5 w-3.5" />
                      legacy
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs font-mono">{u.referred_by || "—"}</TableCell>
                <TableCell className="space-x-1">
                  {!u.is_approved && !u.is_blocked && (
                    <Button size="sm" onClick={() => approve(u.user_id)} data-testid={`approve-user-${u.user_id}`}>{t("ad.btn_approve")}</Button>
                  )}
                  {!u.is_blocked && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => block(u.user_id, false)} data-testid={`block-user-${u.user_id}`}>{t("ad.btn_block")}</Button>
                      <Button size="sm" variant="destructive" onClick={() => block(u.user_id, true)} data-testid={`block-cascade-${u.user_id}`}>{t("ad.btn_block_cascade")}</Button>
                    </>
                  )}
                  {u.is_blocked && (
                    <Button size="sm" variant="outline" onClick={() => unblock(u.user_id)} data-testid={`unblock-user-${u.user_id}`}>{t("ad.btn_unblock")}</Button>
                  )}
                  {isSuperadmin && u.role !== "superadmin" && !u.is_blocked && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-purple-500/60 text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30"
                      onClick={() => impersonate(u)}
                      data-testid={`impersonate-user-${u.user_id}`}
                      title="Sign in as this user (2h session, audited)"
                    >
                      <UserCog className="h-3.5 w-3.5 mr-1" /> Sign in as
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <UsersPaginationBar
        page={page}
        pageSize={pageSize}
        total={total}
        rowsThisPage={users.length}
        loading={loading}
        onPageChange={(next) => load({ page: next })}
        onPageSizeChange={(next) => setPageSize(next)}
      />

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent className="max-w-2xl" data-testid="admin-users-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Permanently delete {selectedUsers.length} user{selectedUsers.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This <strong>cannot be undone</strong>. For each user we also purge: sessions, chats + messages,
              cart, orders, matrimonial profiles, referral invites, reviews and any vendor listings they own.
              Admins, superadmins and QA fixtures are automatically skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-60 overflow-y-auto border border-border/60 rounded-lg divide-y divide-border/60" data-testid="admin-users-bulk-delete-list">
            {selectedUsers.map((u) => (
              <div key={u.user_id} className="px-3 py-1.5 text-sm flex items-center justify-between">
                <span className="truncate">
                  <strong>{u.name}</strong>
                  <span className="text-muted-foreground ml-2">{u.email}</span>
                </span>
                <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
              </div>
            ))}
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">
              Type <code className="px-1 py-0.5 rounded bg-muted font-mono">DELETE</code> to confirm:
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="h-9"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              data-testid="admin-users-bulk-delete-confirm-input"
            />
          </div>

          {report && (
            <div className="text-xs bg-muted/50 rounded p-2 space-y-1" data-testid="admin-users-bulk-delete-report">
              <div>Deleted: <strong>{report.deleted_count}</strong> · Skipped: <strong>{report.skipped?.length ?? 0}</strong></div>
              {(report.skipped || []).length > 0 && (
                <details>
                  <summary className="cursor-pointer">Skip reasons</summary>
                  <ul className="mt-1 pl-4 list-disc">
                    {report.skipped.map((s, i) => <li key={i}><code className="font-mono">{s.user_id}</code> — {s.reason}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-users-bulk-delete-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={confirmText !== "DELETE" || deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="admin-users-bulk-delete-confirm"
            >
              {deleting ? "Deleting…" : `Delete ${selectedUsers.length} permanently`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImpersonateDialog
        open={!!impersonateTarget}
        onOpenChange={(o) => !o && setImpersonateTarget(null)}
        target={impersonateTarget}
        onSuccess={onImpersonateSuccess}
      />
    </Card>
  );
}

/**
 * UsersPaginationBar — page navigation + page-size chooser.
 * Default 500 rows/page as agreed with the superadmin.
 */
function UsersPaginationBar({ page, pageSize, total, rowsThisPage, loading, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + rowsThisPage;
  const canPrev = page > 1 && !loading;
  const canNext = page < totalPages && !loading;

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-3 border-t border-border/60 text-xs" data-testid="admin-users-pagination">
      <div className="text-muted-foreground">
        Showing <strong className="text-foreground">{from.toLocaleString()}</strong>–<strong className="text-foreground">{to.toLocaleString()}</strong>{" "}
        of <strong className="text-foreground">{total.toLocaleString()}</strong> user{total === 1 ? "" : "s"}
        {loading && <span className="ml-2 italic">loading…</span>}
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Rows / page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            data-testid="admin-users-page-size"
            disabled={loading}
          >
            {[100, 250, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => onPageChange(1)} data-testid="admin-users-page-first">« First</Button>
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => onPageChange(page - 1)} data-testid="admin-users-page-prev">‹ Prev</Button>
          <span className="px-3 font-medium" data-testid="admin-users-page-indicator">
            Page {page} / {totalPages}
          </span>
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => onPageChange(page + 1)} data-testid="admin-users-page-next">Next ›</Button>
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => onPageChange(totalPages)} data-testid="admin-users-page-last">Last »</Button>
        </div>
      </div>
    </div>
  );
}
